import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { AuthResponse, MeResponse } from "@tw-pulse/shared";
import { api, ApiClientError, getToken, setToken } from "./api";
import { useTheme } from "./theme";

// Rehydration retry policy. A backend restart (or a brief network blip)
// makes `/users/me` fail transiently; we must NOT treat that like a rejected
// token. Retry a few times across a typical dev restart window before giving
// up — and even then, keep the token so a later reload restores the session.
const REHYDRATE_MAX_ATTEMPTS = 5;
const REHYDRATE_RETRY_MS = 1200;

/** Only a 401/403 means the token itself is bad. Everything else is transient. */
function isAuthRejection(err: unknown): boolean {
  return err instanceof ApiClientError && (err.status === 401 || err.status === 403);
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface AuthState {
  status: "loading" | "authenticated" | "anonymous";
  me: MeResponse | null;
}

interface AuthContextValue extends AuthState {
  signIn: (auth: AuthResponse) => void;
  signInWithToken: (token: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", me: null });
  const { adoptFromServer } = useTheme();

  const refresh = async () => {
    if (!getToken()) {
      setState({ status: "anonymous", me: null });
      return;
    }
    for (let attempt = 1; attempt <= REHYDRATE_MAX_ATTEMPTS; attempt++) {
      try {
        const me = await api.me();
        setState({ status: "authenticated", me });
        // Server-side theme wins only when there's no local override; the
        // hook itself guards against overwriting an in-session user toggle.
        adoptFromServer(me.user.theme);
        return;
      } catch (err) {
        if (isAuthRejection(err)) {
          // The token is genuinely invalid/expired — clear it and require
          // re-login. This is the only case that should destroy the session.
          setToken(null);
          setState({ status: "anonymous", me: null });
          return;
        }
        // Transient failure (backend restarting, network blip, 5xx). Keep the
        // token and retry — don't blow away a valid session over a blip.
        if (attempt < REHYDRATE_MAX_ATTEMPTS) await delay(REHYDRATE_RETRY_MS);
      }
    }
    // Still unreachable after retries. Preserve the token (a later reload will
    // restore the session once the server is back) but fall out of "loading"
    // so the UI isn't stuck on a spinner indefinitely.
    setState({ status: "anonymous", me: null });
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signIn: (auth) => {
        setToken(auth.token);
        setState({
          status: "authenticated",
          me: { user: auth.user, tenant: auth.tenant, membership: auth.membership },
        });
        adoptFromServer(auth.user.theme);
      },
      signInWithToken: async (token) => {
        // Used by the OAuth callback page: we already have a session JWT but
        // need to populate user/tenant/membership from /me.
        setToken(token);
        await refresh();
      },
      signOut: () => {
        setToken(null);
        setState({ status: "anonymous", me: null });
      },
      refresh,
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
